/**
 * Modal 메시지 다국어 지원 (동의 모달 등)
 *
 * 사용법:
 * import { getModalMessage } from '@src/shared/constants/modalMessages';
 * const message = getModalMessage('CONSENT_TITLE');
 */

import type { LocalizedMessages } from '../dto/EntityRepository';
import { useLanguageStore, Language } from '../store/languageStore';

export type { Language } from '../store/languageStore';

export type ModalMessageKey =
  | 'CONSENT_TITLE'
  | 'CONSENT_REQUIRED_LABEL'
  | 'CONSENT_OPTIONAL_LABEL'
  | 'CONSENT_AGE'
  | 'CONSENT_TERMS_BEFORE'
  | 'CONSENT_TERMS_LINK'
  | 'CONSENT_TERMS_AFTER'
  | 'CONSENT_PRIVACY_BEFORE'
  | 'CONSENT_PRIVACY_LINK'
  | 'CONSENT_PRIVACY_AFTER'
  | 'CONSENT_PROMO'
  | 'CONSENT_AGREE_ALL_ERROR'
  // Email verification modal
  | 'EMAIL_VERIFY_TITLE'
  | 'EMAIL_VERIFY_INSTRUCTION'
  | 'EMAIL_VERIFY_NO_CODE'
  // Support modal
  | 'SUPPORT_MODAL_TITLE'
  | 'SUPPORT_TYPE_PLACEHOLDER'
  | 'SUPPORT_AREA_LABEL'
  | 'VENUE_REQUEST_PLACEHOLDER'
  | 'EXHIBITION_VENUE_REQUEST_TITLE'
  | 'EXHIBITION_VENUE_REQUEST_TITLE_PLACEHOLDER'
  | 'EXHIBITION_VENUE_REQUEST_PLACEHOLDER'
  | 'VENUE_SEARCH_NO_RESULTS'
  // Reorder modal
  | 'REORDER_TITLE'
  // Create Career modal
  | 'CREATE_CAREER_MOBILE'
  // Add Content modal
  | 'ADD_PROJECT_ELEMENT_MOBILE'
  // Import Station modal
  | 'SELECT_TO_IMPORT_TEXT'
  | 'SELECT_TO_IMPORT_TEXT_BLOCK'
  | 'SELECT_TO_IMPORT_WORK_DETAIL'
  | 'SELECT_TO_IMPORT_EMPTY'
  | 'DETAIL_CUTS'
  | 'TEXT_BLOCKS'
  | 'SELECT_TEXT_BLOCK_TO_ADD'
  | 'FILTER_STATION_TITLE'
  | 'TAG_STATION_TITLE'
  | 'TAG_SELECTED_COUNT'
  | 'FILTER_APPLYING'
  | 'SELECT_TAG_STATION_TITLE'
  | 'TAG_STATION_EMPTY'
  | 'CREATE_TAG_STATION_TITLE'
  | 'QR_CODE_OPTIONS_TITLE'
  | 'USERNAME_PLACEMENT'
  | 'QR_BELOW_LOGO'
  | 'QR_BELOW_QR'
  | 'QR_OFF'
  | 'BACKGROUND'
  | 'TRANSPARENT'
  | 'WHITE'
  | 'CHANGE_PASSWORD_TITLE'
  | 'SET_PASSWORD_TITLE'
  | 'FIND_PASSWORD_TITLE'
  | 'CHANGE_LINK_ID_TITLE'
  | 'CHANGE_USERNAME_TITLE'
  | 'SUBSCRIPTION_TITLE'
  | 'CREATE_BUIK'
  | 'CREATE_BULK_FILENAME_HINT'
  | 'CREATE_BULK_FILENAME_FORMAT'
  | 'LOGIN_TITLE'
  | 'GROUP_MEMBER_LINK_TITLE'
  | 'GROUP_MEMBER_LINK_CONFIRM_TITLE'
  | 'GROUP_MEMBER_LINK_CONFIRM_NOTICE'
  | 'GROUP_MEMBER_LINK_EMPTY'
  | 'ACCESS_BY_INVITATION_TITLE'
  | 'JOIN_WAITLIST_BODY'
  | 'LOGIN_NO_ACCOUNT'
  | 'REGISTER_HAVE_ACCOUNT'
  | 'REGISTER_GUIDED_SIGNUP'
  | 'AUTH_EMAIL_LABEL'
  | 'AUTH_PASSWORD_LABEL'
  | 'AUTH_CONFIRM_PASSWORD_LABEL'
  | 'AUTH_PASSWORD_MISMATCH'
  | 'FORGOT_PASSWORD'
  | 'ENTER_EMAIL'
  | 'ENTER_CURRENT_PASSWORD'
  | 'CREATE_NEW_PASSWORD'
  | 'PLACEHOLDER_TITLE'
  | 'PLACEHOLDER_DESCRIPTION'
  | 'ENTER_FIELD'
  | 'PDF_PROJECT_OPTIONS_TITLE'
  | 'PDF_PROJECT_CV_LABEL'
  | 'PDF_PROJECT_CV_WITHOUT'
  | 'PDF_PROJECT_CV_WITH'
  | 'PDF_PROJECT_CV_LANGUAGE_LABEL'
  | 'ABOUT_PDF_OPTIONS_TITLE'
  | 'EXPORT_LANGUAGE_LABEL'
  | 'EXPORT_LANGUAGE_ORIGINAL'
  | 'EXPORT_LANGUAGE_ENGLISH'
  | 'EXPORT_LANGUAGE_BOTH'
  | 'ABOUT_PDF_LAYOUT_LABEL'
  | 'ABOUT_PDF_LAYOUT_PORTRAIT'
  | 'ABOUT_PDF_LAYOUT_LANDSCAPE'
  | 'WORK_LIST_PDF_SUMMARY_LABEL'
  | 'WORK_LIST_PDF_SELECTION_SUMMARY'
  | 'WORK_LIST_PDF_SELECTED_COUNT'
  | 'WORK_LIST_PDF_PRICE_LABEL'
  | 'WORK_LIST_PDF_TOGGLE_INCLUDE'
  | 'WORK_LIST_PDF_TOGGLE_EXCLUDE'
  | 'WORK_LIST_PDF_NOTE_LABEL'
  | 'WORK_LIST_PDF_CAPTION_LINE_LABEL'
  | 'WORK_LIST_PDF_CAPTION_COLUMN_HEADER'
  | 'WORK_LIST_EXPORT_FORMAT_LABEL'
  | 'WORK_LIST_EXPORT_FORMAT_PDF'
  | 'WORK_LIST_EXPORT_FORMAT_EXCEL'
  | 'WORK_LIST_PDF_SELECT_TITLE'
  | 'WORK_LIST_PDF_INDEXING_PLACEHOLDER'
  | 'WORK_LIST_PDF_EXPORT_TITLE'
  | 'FLOOR_PLAN_MODAL_TITLE'
  | 'FLOOR_PLAN_FORM_TITLE_LABEL'
  | 'FLOOR_PLAN_FORM_TITLE_PLACEHOLDER'
  | 'FLOOR_PLAN_FORM_SUBTITLE_LABEL'
  | 'FLOOR_PLAN_FORM_SUBTITLE_PLACEHOLDER'
  | 'FLOOR_PLAN_FORM_DATE_LABEL'
  | 'FLOOR_PLAN_FORM_DATE_PLACEHOLDER'
  | 'FLOOR_PLAN_FORM_GALLERY_LABEL'
  | 'FLOOR_PLAN_FORM_GALLERY_PLACEHOLDER'
  | 'FLOOR_PLAN_FORM_LOCATION_LABEL'
  | 'FLOOR_PLAN_FORM_LOCATION_PLACEHOLDER'
  | 'FLOOR_PLAN_FORM_CAPTION_LABEL'
  | 'FLOOR_PLAN_FORM_CAPTION_COLUMN'
  | 'FLOOR_PLAN_FORM_CAPTION_ROW'
  | 'SEO_DESCRIPTION_HINT'
  | 'SEO_DESCRIPTION_EN_HINT'
  ;

const modalMessages: LocalizedMessages<ModalMessageKey> = {
  CONSENT_TITLE: {
    [Language.EN]: "We need your consent to proceed",
    [Language.KR]: "서비스 이용 동의",
  },
  CONSENT_REQUIRED_LABEL: {
    [Language.EN]: "[Required]",
    [Language.KR]: "[필수]",
  },
  CONSENT_OPTIONAL_LABEL: {
    [Language.EN]: "[Optional]",
    [Language.KR]: "[선택]",
  },
  CONSENT_AGE: {
    [Language.EN]: "I confirm that I am at least 14 years old.",
    [Language.KR]: "만 14세 이상입니다.",
  },
  CONSENT_TERMS_BEFORE: {
    [Language.EN]: "I agree to the ",
    [Language.KR]: "다음",
  },
  CONSENT_TERMS_LINK: {
    [Language.EN]: "Terms of Service",
    [Language.KR]: "이용약관",
  },
  CONSENT_TERMS_AFTER: {
    [Language.EN]: " for account and service.",
    [Language.KR]: "에 동의합니다.",
  },
  CONSENT_PRIVACY_BEFORE: {
    [Language.EN]: "I agree to ",
    [Language.KR]: "다음",
  },
  CONSENT_PRIVACY_LINK: {
    [Language.EN]: "personal information use",
    [Language.KR]: "개인정보 처리방침",
  },
  CONSENT_PRIVACY_AFTER: {
    [Language.EN]: " for account and service.",
    [Language.KR]: "에 동의합니다.",
  },
  CONSENT_PROMO: {
    [Language.EN]: "I agree to receive promotional emails.",
    [Language.KR]: "프로모션 이메일 수신에 동의합니다.",
  },
  CONSENT_AGREE_ALL_ERROR: {
    [Language.EN]: "Please agree to all required items.",
    [Language.KR]: "필수 항목에 모두 동의해주세요.",
  },
  // Email verification modal
  EMAIL_VERIFY_TITLE: {
    [Language.EN]: "Email Verification",
    [Language.KR]: "이메일 인증",
  },
  EMAIL_VERIFY_INSTRUCTION: {
    [Language.EN]: "Enter 6-digit code sent to your email address.",
    [Language.KR]: "이메일로 전송된 6자리 코드를 입력해주세요.",
  },
  EMAIL_VERIFY_NO_CODE: {
    [Language.EN]: "Didn't receive the code?",
    [Language.KR]: "코드를 못 받았나요?",
  },
  // Support modal
  SUPPORT_MODAL_TITLE: {
    [Language.EN]: "Feedback is always welcome",
    [Language.KR]: "의견과 제안을 환영합니다",
  },
  SUPPORT_TYPE_PLACEHOLDER: {
    [Language.EN]: "Inquiry type",
    [Language.KR]: "문의 유형",
  },
  SUPPORT_AREA_LABEL: {
    [Language.EN]: "Related page",
    [Language.KR]: "관련 페이지",
  },
  VENUE_REQUEST_PLACEHOLDER: {
    [Language.EN]: "Tell us about a space you'd like to add. Share whatever you know: its name, area, map link, or Instagram.",
    [Language.KR]: "추가하고 싶은 공간을 알려주세요. 공간 이름, 지역, 지도 링크, 인스타그램 중 아는 정보만 남겨주셔도 괜찮아요.",
  },
  EXHIBITION_VENUE_REQUEST_TITLE: {
    [Language.EN]: "Request to register an exhibition or venue",
    [Language.KR]: "전시·공간 등록 요청",
  },
  EXHIBITION_VENUE_REQUEST_TITLE_PLACEHOLDER: {
    [Language.EN]: "Exhibition or venue title",
    [Language.KR]: "전시명 또는 공간명",
  },
  EXHIBITION_VENUE_REQUEST_PLACEHOLDER: {
    [Language.EN]: "Describe the exhibition or venue you wish to have registered.\nTitle, dates, or a related Instagram account will suffice.",
    [Language.KR]: "등록을 요청하실 전시 또는 공간을 적어 주세요.\n제목, 기간, 관련 인스타그램 계정만으로 충분합니다.",
  },
  VENUE_SEARCH_NO_RESULTS: {
    [Language.EN]: "No results.",
    [Language.KR]: "검색 결과가 없습니다.",
  },
  // Reorder modal
  REORDER_TITLE: {
    [Language.EN]: "Reorder {title}",
    [Language.KR]: "{title} 순서 변경",
  },
  // Create Career modal
  CREATE_CAREER_MOBILE: {
    [Language.EN]: "Create Career",
    [Language.KR]: "Career 추가",
  },
  // Add Content modal
  ADD_PROJECT_ELEMENT_MOBILE: {
    [Language.EN]: "Add Element",
    [Language.KR]: "Element 추가",
  },
  // Import Station modal
  SELECT_TO_IMPORT_TEXT: {
    [Language.EN]: "Select Text to Import",
    [Language.KR]: "가져올 Text 선택하기",
  },
  SELECT_TO_IMPORT_TEXT_BLOCK: {
    [Language.EN]: "Select Text Block to Import",
    [Language.KR]: "가져올 Text Block 선택하기",
  },
  SELECT_TO_IMPORT_WORK_DETAIL: {
    [Language.EN]: "Select Work / Detail to Import",
    [Language.KR]: "가져올 Work / Detail 선택하기",
  },
  SELECT_TO_IMPORT_EMPTY: {
    [Language.EN]: "Select Work or Detail Cut",
    [Language.KR]: "Work 또는 Detail Cut을 선택해주세요.",
  },
  DETAIL_CUTS: {
    [Language.EN]: "Detail cut",
    [Language.KR]: "Detail cut",
  },
  TEXT_BLOCKS: {
    [Language.EN]: "Text block",
    [Language.KR]: "Text block",
  },
  SELECT_TEXT_BLOCK_TO_ADD: {
    [Language.EN]: "Select Text Block to Add",
    [Language.KR]: "추가할 Text Block 선택하기",
  },
  FILTER_STATION_TITLE: {
    [Language.EN]: "Filter Station",
    [Language.KR]: "검색 조건",
  },
  TAG_STATION_TITLE: {
    [Language.EN]: "Labels",
    [Language.KR]: "라벨",
  },
  TAG_SELECTED_COUNT: {
    [Language.EN]: "{count} selected",
    [Language.KR]: "{count}개 선택",
  },
  FILTER_APPLYING: {
    [Language.EN]: "Active",
    [Language.KR]: "적용됨",
  },
  SELECT_TAG_STATION_TITLE: {
    [Language.EN]: "Select Labels",
    [Language.KR]: "라벨 선택하기",
  },
  TAG_STATION_EMPTY: {
    [Language.EN]: "No labels yet",
    [Language.KR]: "라벨이 없습니다",
  },
  CREATE_TAG_STATION_TITLE: {
    [Language.EN]: "Create Label",
    [Language.KR]: "라벨 생성하기",
  },
  QR_CODE_OPTIONS_TITLE: {
    [Language.EN]: "QR Code Options",
    [Language.KR]: "QR 코드 옵션",
  },
  USERNAME_PLACEMENT: {
    [Language.EN]: "Username Placement",
    [Language.KR]: "Username 위치",
  },
  QR_BELOW_LOGO: {
    [Language.EN]: "Below the logo",
    [Language.KR]: "Logo 아래",
  },
  QR_BELOW_QR: {
    [Language.EN]: "Below the QR code",
    [Language.KR]: "QR 코드 아래",
  },
  QR_OFF: {
    [Language.EN]: "Off",
    [Language.KR]: "없음",
  },
  BACKGROUND: {
    [Language.EN]: "Background",
    [Language.KR]: "배경색",
  },
  TRANSPARENT: {
    [Language.EN]: "Transparent",
    [Language.KR]: "투명",
  },
  WHITE: {
    [Language.EN]: "White",
    [Language.KR]: "기본",
  },
  CHANGE_PASSWORD_TITLE: {
    [Language.EN]: "Change Password",
    [Language.KR]: "비밀번호 변경",
  },
  SET_PASSWORD_TITLE: {
    [Language.EN]: "Set Password",
    [Language.KR]: "비밀번호 설정",
  },
  FIND_PASSWORD_TITLE: {
    [Language.EN]: "Find Password",
    [Language.KR]: "비밀번호 찾기",
  },
  CHANGE_LINK_ID_TITLE: {
    [Language.EN]: "Change Link ID",
    [Language.KR]: "Link ID 변경",
  },
  CHANGE_USERNAME_TITLE: {
    [Language.EN]: "Change Username",
    [Language.KR]: "Username 변경",
  },
  SUBSCRIPTION_TITLE: {
    [Language.EN]: "Subscription",
    [Language.KR]: "구독하기",
  },
  CREATE_BUIK: {
    [Language.EN]: "Create {title}",
    [Language.KR]: "{title} 일괄 생성하기",
  },
  CREATE_BULK_FILENAME_HINT: {
    [Language.EN]: "Predefined filename formats help with auto-fill.",
    [Language.KR]: "정해진 형식의 파일명은 자동 입력에 도움이 됩니다.",
  },
  CREATE_BULK_FILENAME_FORMAT: {
    [Language.EN]: "Title, Year, Materials, Size (H × W × D cm)",
    [Language.KR]: "작품명, 제작연도, 재료, 크기 (H × W × D cm)",
  },
  LOGIN_TITLE: {
    [Language.EN]: "Login",
    [Language.KR]: "로그인",
  },
  GROUP_MEMBER_LINK_TITLE: {
    [Language.EN]: "Connect account",
    [Language.KR]: "계정 연결",
  },
  GROUP_MEMBER_LINK_CONFIRM_TITLE: {
    [Language.EN]: "Overwrite profile",
    [Language.KR]: "프로필 덮어쓰기",
  },
  GROUP_MEMBER_LINK_CONFIRM_NOTICE: {
    [Language.EN]: "These values will overwrite the current group member profile.",
    [Language.KR]: "아래 값으로 현재 그룹 멤버 프로필을 덮어씁니다.",
  },
  GROUP_MEMBER_LINK_EMPTY: {
    [Language.EN]: "(empty)",
    [Language.KR]: "(비워짐)",
  },
  ACCESS_BY_INVITATION_TITLE: {
    [Language.EN]: "Access by Invitation",
    [Language.KR]: "대기 안내",
  },
  JOIN_WAITLIST_BODY: {
    [Language.EN]: `Thank you for your interest in Architrave.\n\n\nTo preserve the integrity of the experience, access is being introduced gradually.\n\n\nYou may join the waitlist below.\nInvitations are extended by email, in the order received.`,
    [Language.KR]: `Architrave에 관심을 가져주셔서 감사합니다.\n\n\n더 나은 사용 경험을 위해 현재는 초대 기반으로 운영되고 있습니다.\n\n아래에서 대기 목록에 등록해 주세요.\n등록해 주신 순서대로 이메일로 초대해 드립니다.`,
  },
  LOGIN_NO_ACCOUNT: {
    [Language.EN]: "Don't have an account?",
    [Language.KR]: "계정이 없으신가요?",
  },
  REGISTER_HAVE_ACCOUNT: {
    [Language.EN]: "Already have an account?",
    [Language.KR]: "이미 계정이 있나요?",
  },
  REGISTER_GUIDED_SIGNUP: {
    [Language.EN]: "Use guided signup",
    [Language.KR]: "단계별로 가입하기",
  },
  AUTH_EMAIL_LABEL: {
    [Language.EN]: "Email",
    [Language.KR]: "이메일",
  },
  AUTH_PASSWORD_LABEL: {
    [Language.EN]: "Password",
    [Language.KR]: "비밀번호",
  },
  AUTH_CONFIRM_PASSWORD_LABEL: {
    [Language.EN]: "Confirm Password",
    [Language.KR]: "비밀번호 확인",
  },
  AUTH_PASSWORD_MISMATCH: {
    [Language.EN]: "Passwords do not match.",
    [Language.KR]: "비밀번호가 일치하지 않습니다.",
  },
  FORGOT_PASSWORD: {
    [Language.EN]: "Forgot Password?",
    [Language.KR]: "비밀번호를 잊으셨나요?",
  },
  ENTER_EMAIL: {
    [Language.EN]: "Enter Email",
    [Language.KR]: "이메일 입력",
  },
  ENTER_CURRENT_PASSWORD: {
    [Language.EN]: "Enter Current Password",
    [Language.KR]: "현재 비밀번호 입력",
  },
  CREATE_NEW_PASSWORD: {
    [Language.EN]: "Create New Password",
    [Language.KR]: "새 비밀번호 입력",
  },
  PLACEHOLDER_TITLE: {
    [Language.EN]: "Enter a title",
    [Language.KR]: "문의 제목을 입력해 주세요",
  },
  PLACEHOLDER_DESCRIPTION: {
    [Language.EN]: "Share your message",
    [Language.KR]: "궁금한 점이나 전하고 싶은 내용을 적어 주세요",
  },
  ENTER_FIELD: {
    [Language.EN]: "Enter {field}",
    [Language.KR]: "{field} 입력",
  },
  PDF_PROJECT_OPTIONS_TITLE: {
    [Language.EN]: "PDF Options",
    [Language.KR]: "PDF 생성 옵션",
  },
  PDF_PROJECT_CV_LABEL: {
    [Language.EN]: "CV",
    [Language.KR]: "CV 포함 여부",
  },
  PDF_PROJECT_CV_WITHOUT: {
    [Language.EN]: "Omit",
    [Language.KR]: "미포함",
  },
  PDF_PROJECT_CV_WITH: {
    [Language.EN]: "Include",
    [Language.KR]: "포함",
  },
  PDF_PROJECT_CV_LANGUAGE_LABEL: {
    [Language.EN]: "CV language",
    [Language.KR]: "CV 언어",
  },
  ABOUT_PDF_OPTIONS_TITLE: {
    [Language.EN]: "Export Options",
    [Language.KR]: "내보내기 옵션",
  },
  EXPORT_LANGUAGE_LABEL: {
    [Language.EN]: "Language",
    [Language.KR]: "언어",
  },
  EXPORT_LANGUAGE_ORIGINAL: {
    [Language.EN]: "Original",
    [Language.KR]: "기본",
  },
  EXPORT_LANGUAGE_ENGLISH: {
    [Language.EN]: "English",
    [Language.KR]: "영문",
  },
  EXPORT_LANGUAGE_BOTH: {
    [Language.EN]: "Original + English",
    [Language.KR]: "기본 + 영문",
  },
  ABOUT_PDF_LAYOUT_LABEL: {
    [Language.EN]: "Orientation",
    [Language.KR]: "방향",
  },
  ABOUT_PDF_LAYOUT_PORTRAIT: {
    [Language.EN]: "Portrait",
    [Language.KR]: "세로",
  },
  ABOUT_PDF_LAYOUT_LANDSCAPE: {
    [Language.EN]: "Landscape",
    [Language.KR]: "가로",
  },
  WORK_LIST_PDF_SUMMARY_LABEL: {
    [Language.EN]: "Summary",
    [Language.KR]: "요약",
  },
  WORK_LIST_PDF_SELECTION_SUMMARY: {
    [Language.EN]: "{count} works selected, {pages} pages expected.",
    [Language.KR]: "{count}개 작품 선택됨, 예상 {pages}장",
  },
  WORK_LIST_PDF_SELECTED_COUNT: {
    [Language.EN]: "{count} selected",
    [Language.KR]: "{count}개 선택됨",
  },
  WORK_LIST_PDF_PRICE_LABEL: {
    [Language.EN]: "Price",
    [Language.KR]: "작품 가격",
  },
  WORK_LIST_PDF_TOGGLE_INCLUDE: {
    [Language.EN]: "Include",
    [Language.KR]: "포함",
  },
  WORK_LIST_PDF_TOGGLE_EXCLUDE: {
    [Language.EN]: "Omit",
    [Language.KR]: "생략",
  },
  WORK_LIST_PDF_NOTE_LABEL: {
    [Language.EN]: "Note",
    [Language.KR]: "비고",
  },
  WORK_LIST_PDF_CAPTION_LINE_LABEL: {
    [Language.EN]: "Caption line",
    [Language.KR]: "캡션 한 줄",
  },
  WORK_LIST_PDF_CAPTION_COLUMN_HEADER: {
    [Language.EN]: "Caption",
    [Language.KR]: "캡션",
  },
  WORK_LIST_EXPORT_FORMAT_LABEL: {
    [Language.EN]: "File format",
    [Language.KR]: "파일 형식",
  },
  WORK_LIST_EXPORT_FORMAT_PDF: {
    [Language.EN]: "PDF (.pdf)",
    [Language.KR]: "PDF (.pdf)",
  },
  WORK_LIST_EXPORT_FORMAT_EXCEL: {
    [Language.EN]: "Excel (.xlsx)",
    [Language.KR]: "Excel (.xlsx)",
  },
  WORK_LIST_PDF_SELECT_TITLE: {
    [Language.EN]: "Select Works",
    [Language.KR]: "작품 선택하기",
  },
  WORK_LIST_PDF_INDEXING_PLACEHOLDER: {
    [Language.EN]: "Select works from the left.",
    [Language.KR]: "왼쪽에서 작품을 선택하세요.",
  },
  WORK_LIST_PDF_EXPORT_TITLE: {
    [Language.EN]: "Export Work List",
    [Language.KR]: "작품 목록 내보내기",
  },
  FLOOR_PLAN_MODAL_TITLE: {
    [Language.EN]: "Export Floor plan",
    [Language.KR]: "작품 배치도 내보내기",
  },
  FLOOR_PLAN_FORM_TITLE_LABEL: {
    [Language.EN]: "Title",
    [Language.KR]: "제목",
  },
  FLOOR_PLAN_FORM_TITLE_PLACEHOLDER: {
    [Language.EN]: "Enter title",
    [Language.KR]: "제목 입력",
  },
  FLOOR_PLAN_FORM_SUBTITLE_LABEL: {
    [Language.EN]: "Subtitle",
    [Language.KR]: "부제목",
  },
  FLOOR_PLAN_FORM_SUBTITLE_PLACEHOLDER: {
    [Language.EN]: "Enter subtitle",
    [Language.KR]: "부제목, 작가명 등",
  },
  FLOOR_PLAN_FORM_DATE_LABEL: {
    [Language.EN]: "Date",
    [Language.KR]: "날짜",
  },
  FLOOR_PLAN_FORM_DATE_PLACEHOLDER: {
    [Language.EN]: "Enter date",
    [Language.KR]: "날짜 입력",
  },
  FLOOR_PLAN_FORM_GALLERY_LABEL: {
    [Language.EN]: "Gallery",
    [Language.KR]: "전시 장소",
  },
  FLOOR_PLAN_FORM_GALLERY_PLACEHOLDER: {
    [Language.EN]: "Venue name",
    [Language.KR]: "전시 장소명(갤러리 및 미술관, 전시장 등)",
  },
  FLOOR_PLAN_FORM_LOCATION_LABEL: {
    [Language.EN]: "Location",
    [Language.KR]: "전시장 주소",
  },
  FLOOR_PLAN_FORM_LOCATION_PLACEHOLDER: {
    [Language.EN]: "Enter address",
    [Language.KR]: "전시장 주소(시, 구, 동 등)",
  },
  FLOOR_PLAN_FORM_CAPTION_LABEL: {
    [Language.EN]: "Caption",
    [Language.KR]: "캡션",
  },
  FLOOR_PLAN_FORM_CAPTION_COLUMN: {
    [Language.EN]: "Column",
    [Language.KR]: "열",
  },
  FLOOR_PLAN_FORM_CAPTION_ROW: {
    [Language.EN]: "Row",
    [Language.KR]: "행",
  },
  SEO_DESCRIPTION_HINT: {
    [Language.EN]:
      "Main description for search results (e.g. Google). Write it in your preferred language. \nTwo sentences and 120+ characters recommended.",
    [Language.KR]:
      "검색 결과에서 사용하는 기본 설명입니다. 원하는 언어로 입력해 주세요. \n두 문장 이상, 120자 이상을 권장합니다.",
  },
  SEO_DESCRIPTION_EN_HINT: {
    [Language.EN]:
      "English description for English-language search results (e.g. Google). \nAdd it if you want your page understood in English searches.",
    [Language.KR]:
      "영어 검색 결과에서 페이지를 설명하는 데 사용됩니다. \n영문 검색 노출이 필요하다면 영어로 입력해 주세요.",
  },
};

/**
 * Modal 메시지를 가져오는 함수
 * 언어는 languageStore에서 자동으로 가져옵니다.
 * @param key 메시지 키
 * @param params 메시지에 포함될 파라미터 (예: {title: "Project"})
 * @returns 번역된 메시지
 */
export function getModalMessage(
  key: ModalMessageKey,
  params?: Record<string, string | number>
): string {
  const language = useLanguageStore.getState().language;
  const message = modalMessages[key];
  
  if (!message) {
    console.warn(`Modal message key "${key}" not found.`);
    return key;
  }
  
  // 선택한 언어가 없으면 영어로 폴백
  let translatedMessage = message[language] || message[Language.EN];
  
  // 파라미터 치환 (예: {title} → 실제 값)
  if (params) {
    Object.entries(params).forEach(([paramKey, paramValue]) => {
      const safeValue = paramValue !== undefined && paramValue !== null
        ? String(paramValue)
        : '';
      const pattern = new RegExp(`\\{${paramKey.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\}`, 'g');
      translatedMessage = translatedMessage.replace(pattern, safeValue);
    });
  }
  
  return translatedMessage;
}
